import { useTranslation } from 'react-i18next';
import styles from './MailCardCardLabelQuestion.module.css';

interface MailCardCardLabelQuestionProps {
  /** The stripped cardLabel noun. Non-null by mount precondition:
   *  the parent only mounts this when cardLabelNoun !== null. */
  cardLabelNoun: string;
  onInclude: () => void;
  onSkip: () => void;
}

/**
 * Q2 surface for MailCardSheet (known-sender only).
 *
 * Asks whether to put the curated cardLabel noun on the card's
 * "about" line, mirroring the text/share card's noun question.
 * SOURCE: prayer.parsed.cardLabel (English-only, allowlisted),
 * stripped via stripCardLabelDisambig in the parent and passed
 * down as cardLabelNoun. This replaced the prior gardenSummary
 * source per the 2026-05-29 product decision: the mail card's
 * "about" now uses the same noun source as texting a prayer.
 *
 * MOUNT PRECONDITION: parent only mounts this when cardLabelNoun
 * is non-null (English + a curated noun exists). For es/pt and for
 * abstract / no-match English prayers cardLabelNoun is null and the
 * parent skips Q2 entirely (advanceFromQ1's skip branch).
 *
 * i18n keys consumed: mailCard.cardLabelPrompt,
 * mailCard.cardLabelIncludeBtn, mailCard.cardLabelSkipBtn,
 * mailCard.cardLabelPreviewHelper (with {{noun}} interpolation).
 */
export default function MailCardCardLabelQuestion({
  cardLabelNoun,
  onInclude,
  onSkip,
}: MailCardCardLabelQuestionProps) {
  const { t } = useTranslation();

  return (
    <section
      className={styles.question}
      aria-labelledby="mailcard-cardlabel-prompt"
    >
      <h2 className={styles.prompt} id="mailcard-cardlabel-prompt">
        {t('mailCard.cardLabelPrompt')}
      </h2>
      <p className={styles.helper}>
        {t('mailCard.cardLabelPreviewHelper', { noun: cardLabelNoun })}
      </p>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          onClick={onInclude}
          aria-label={t('mailCard.cardLabelIncludeBtn')}
        >
          {t('mailCard.cardLabelIncludeBtn')}
        </button>
        <button
          type="button"
          className={styles.secondary}
          onClick={onSkip}
          aria-label={t('mailCard.cardLabelSkipBtn')}
        >
          {t('mailCard.cardLabelSkipBtn')}
        </button>
      </div>
    </section>
  );
}
